import {
  collectionGroup,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  Timestamp,
  getDoc,
  orderBy,
  limit,
  documentId,
  collection,
} from "firebase/firestore";
import { db } from "../config/firebase";
import type { StoryWithBar, StoryStats } from "../types/story.types";

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const getBarNames = async (barIds: string[]): Promise<Map<string, string>> => {
  const names = new Map<string, string>();
  const uniqueBarIds = [...new Set(barIds.filter(Boolean))];

  for (const batch of chunkArray(uniqueBarIds, 10)) {
    const barsSnapshot = await getDocs(
      query(collection(db, "bars"), where(documentId(), "in", batch)),
    );

    barsSnapshot.docs.forEach((barDoc) => {
      names.set(barDoc.id, (barDoc.data().name as string) || barDoc.id);
    });
  }

  return names;
};

/**
 * Lädt alle Stories über alle Bars
 */
export const getAllStories = async (): Promise<StoryWithBar[]> => {
  try {
    const storiesQuery = query(
      collectionGroup(db, "stories"),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const snapshot = await getDocs(storiesQuery);
    const stories: StoryWithBar[] = [];
    const barIds = snapshot.docs
      .map((storyDoc) => storyDoc.ref.parent.parent?.id || "")
      .filter(Boolean);
    const barNames = await getBarNames(barIds);

    for (const storyDoc of snapshot.docs) {
      const storyData = storyDoc.data();
      const barId = storyDoc.ref.parent.parent?.id || "";
      const barName = barNames.get(barId) || "Unknown Bar";

      // Check if expired
      const now = Timestamp.now();
      const isExpired =
        storyData.expiresAt && storyData.expiresAt.toDate() < now.toDate();

      stories.push({
        id: storyDoc.id,
        barId,
        barName,
        imageUrl: storyData.imageUrl,
        videoUrl: storyData.videoUrl,
        textOverlay: storyData.textOverlay,
        filter: storyData.filter,
        link: storyData.link,
        durationType: storyData.durationType,
        expiresAt: storyData.expiresAt,
        customDuration: storyData.customDuration,
        viewCount: storyData.viewCount || 0,
        viewedBy: storyData.viewedBy || [],
        isActive: storyData.isActive !== false,
        isExpired: isExpired || false,
        createdBy: storyData.createdBy || "",
        createdByName: storyData.createdByName,
        createdAt: storyData.createdAt,
        updatedAt: storyData.updatedAt || storyData.createdAt,
      });
    }

    return stories;
  } catch (error) {
    console.error("Error fetching all stories:", error);
    throw error;
  }
};

/**
 * Lädt nur aktive (nicht abgelaufene) Stories
 */
export const getActiveStories = async (): Promise<StoryWithBar[]> => {
  try {
    const now = Timestamp.now();
    const storiesQuery = query(
      collectionGroup(db, "stories"),
      where("isActive", "==", true),
      where("expiresAt", ">", now),
      orderBy("expiresAt", "asc"),
      limit(100)
    );

    const snapshot = await getDocs(storiesQuery);
    const stories: StoryWithBar[] = [];
    const barIds = snapshot.docs
      .map((storyDoc) => storyDoc.ref.parent.parent?.id || "")
      .filter(Boolean);
    const barNames = await getBarNames(barIds);

    for (const storyDoc of snapshot.docs) {
      const storyData = storyDoc.data();
      const barId = storyDoc.ref.parent.parent?.id || "";
      const barName = barNames.get(barId) || "Unknown Bar";

      stories.push({
        id: storyDoc.id,
        barId,
        barName,
        imageUrl: storyData.imageUrl,
        videoUrl: storyData.videoUrl,
        textOverlay: storyData.textOverlay,
        filter: storyData.filter,
        link: storyData.link,
        durationType: storyData.durationType,
        expiresAt: storyData.expiresAt,
        customDuration: storyData.customDuration,
        viewCount: storyData.viewCount || 0,
        viewedBy: storyData.viewedBy || [],
        isActive: true,
        isExpired: false,
        createdBy: storyData.createdBy || "",
        createdByName: storyData.createdByName,
        createdAt: storyData.createdAt,
        updatedAt: storyData.updatedAt || storyData.createdAt,
      });
    }

    return stories;
  } catch (error) {
    console.error("Error fetching active stories:", error);
    throw error;
  }
};

/**
 * Lädt alle Stories für eine spezifische Bar
 */
export const getStoriesByBar = async (
  barId: string
): Promise<StoryWithBar[]> => {
  try {
    const allStories = await getAllStories();
    return allStories.filter((story) => story.barId === barId);
  } catch (error) {
    console.error("Error fetching stories by bar:", error);
    throw error;
  }
};

/**
 * Lädt alle Bars die Stories haben (nur geclaimte Bars)
 */
export const getBarsWithStories = async (): Promise<
  Array<{
    barId: string;
    barName: string;
    storyCount: number;
    isClaimed: boolean;
  }>
> => {
  try {
    const allStories = await getAllStories();

    // Gruppiere Stories nach barId
    const barMap = new Map<string, { barName: string; count: number }>();

    for (const story of allStories) {
      if (story.barId) {
        const existing = barMap.get(story.barId);
        if (existing) {
          existing.count++;
        } else {
          barMap.set(story.barId, { barName: story.barName, count: 1 });
        }
      }
    }

    // Konvertiere zu Array und prüfe isClaimed
    const barIds = [...barMap.keys()];
    const claimedBars = new Set<string>();

    for (const batch of chunkArray(barIds, 10)) {
      const barsSnapshot = await getDocs(
        query(collection(db, "bars"), where(documentId(), "in", batch)),
      );

      barsSnapshot.docs.forEach((barDoc) => {
        if (barDoc.data().isClaimed === true) {
          claimedBars.add(barDoc.id);
        }
      });
    }

    const barsWithStories = [];
    for (const [barId, data] of barMap.entries()) {
      if (claimedBars.has(barId)) {
        barsWithStories.push({
          barId,
          barName: data.barName,
          storyCount: data.count,
          isClaimed: true,
        });
      }
    }

    return barsWithStories.sort((a, b) => b.storyCount - a.storyCount);
  } catch (error) {
    console.error("Error fetching bars with stories:", error);
    throw error;
  }
};

/**
 * Lädt abgelaufene Stories
 */
export const getExpiredStories = async (): Promise<StoryWithBar[]> => {
  try {
    const now = Timestamp.now();
    const storiesQuery = query(
      collectionGroup(db, "stories"),
      where("expiresAt", "<=", now),
      orderBy("expiresAt", "desc"),
      limit(100)
    );

    const snapshot = await getDocs(storiesQuery);
    const stories: StoryWithBar[] = [];

    for (const storyDoc of snapshot.docs) {
      const storyData = storyDoc.data();
      const barId = storyDoc.ref.parent.parent?.id || "";

      let barName = "Unknown Bar";
      if (barId) {
        const barDoc = await getDoc(doc(db, "bars", barId));
        if (barDoc.exists()) {
          barName = barDoc.data().name || barId;
        }
      }

      stories.push({
        id: storyDoc.id,
        barId,
        barName,
        imageUrl: storyData.imageUrl,
        videoUrl: storyData.videoUrl,
        textOverlay: storyData.textOverlay,
        filter: storyData.filter,
        link: storyData.link,
        durationType: storyData.durationType,
        expiresAt: storyData.expiresAt,
        customDuration: storyData.customDuration,
        viewCount: storyData.viewCount || 0,
        viewedBy: storyData.viewedBy || [],
        isActive: storyData.isActive !== false,
        isExpired: true,
        createdBy: storyData.createdBy || "",
        createdByName: storyData.createdByName,
        createdAt: storyData.createdAt,
        updatedAt: storyData.updatedAt || storyData.createdAt,
      });
    }

    return stories;
  } catch (error) {
    console.error("Error fetching expired stories:", error);
    throw error;
  }
};

/**
 * Löscht eine Story
 */
export const deleteStory = async (
  barId: string,
  storyId: string
): Promise<void> => {
  try {
    const storyRef = doc(db, "bars", barId, "stories", storyId);
    await deleteDoc(storyRef);
  } catch (error) {
    console.error("Error deleting story:", error);
    throw error;
  }
};

/**
 * Lädt Story Stats
 */
export const getStoryStats = async (): Promise<StoryStats> => {
  try {
    const allStories = await getAllStories();
    const activeStories = allStories.filter((s) => !s.isExpired && s.isActive);
    const expiredStories = allStories.filter((s) => s.isExpired);
    const totalViews = allStories.reduce(
      (sum, s) => sum + (s.viewCount || 0),
      0
    );
    const avgViewsPerStory =
      allStories.length > 0 ? totalViews / allStories.length : 0;

    return {
      totalStories: allStories.length,
      activeStories: activeStories.length,
      expiredStories: expiredStories.length,
      totalViews,
      avgViewsPerStory: Math.round(avgViewsPerStory),
    };
  } catch (error) {
    console.error("Error fetching story stats:", error);
    return {
      totalStories: 0,
      activeStories: 0,
      expiredStories: 0,
      totalViews: 0,
      avgViewsPerStory: 0,
    };
  }
};
